/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import React from 'react'
import Link from 'components/atoms/Link'
import Toast from 'components/atoms/Toast'
import routes from 'components/lab/constants/routes'
import { useUnapplyBillingPaymentApplicationMutation } from 'store/services/billingPayments'
import { useGetAllBillingProceduresListQuery } from 'store/services/billing-procedures'
import {
  useGeneratePaymentReceiptMutation,
  useLazyGetPaymentReceiptDownloadQuery,
} from 'store/services/paymentReceipts'
import { BillingChargeDto, BillingNoteDto, ChargeAccountRow, } from 'store/types'
import { nc } from 'helpers/nameHelper'
import { StatePill } from 'components/lab/atoms/StatePill'
import { NoteEntity, NotesIndicator } from 'components/lab/billingNotes/BillingNotes'
import { BillingNotesModal, NotesTarget } from 'components/lab/billingNotes/BillingNotesModal'
import {
  chargeBadgeStyle,
  getCobActionLabel,
  getCobActionTitle,
  getRenderingAndBilling,
  getShortChargeIdValue,
  RESPONSIBILITY_LABELS,
  responsibilityColor,
} from '../billingCharge/utils'
import { AttachmentsIndicator, ChargeAttachmentsModal } from './ChargeAttachmentsModal'
import { RefundConfirmModal } from './RefundConfirmModal'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from '../user/permissions'
import { serviceLineGrid, ServiceLineRow } from './ServiceLineRow'
import { formatCalendarDate, formatEasternDate, sortByDateOfService } from 'helpers/dates'

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`

const TableHeader = styled.thead`
  background-color: #f8f9fa;
  z-index: 10;
`

const TableHeaderCell = styled.th`
  text-align: left;
  padding: 0.5rem 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 2;
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 1px solid #dee2e6;
`

const TableCell = styled.td`
  padding: 0.75rem 0.5rem;
  vertical-align: middle;
  position: relative;
`

const ExpandedCell = styled.td`
  padding: 0;
  background-color: #fbfcfe;
`

interface Props {
  rows: ChargeAccountRow[]
  isLoading: boolean
  patientId: number
  // The aggregate-computed COB fields ride on the charges query, not on the
  // charge-account read model — merged in here for the submit action.
  chargesById: Record<string, BillingChargeDto>
  submittingChargeId: string | null
  // Submits the next COB hop — the server-computed cobRank on the charge says
  // whether that is the secondary or the tertiary payer.
  onSubmitCob: (charge: BillingChargeDto, options?: { resubmit?: boolean }) => void
  onRefresh?: () => void
  // Opens the Create Payment form on this charge with the payer picked, for
  // an EOB that paid several of its lines at once.
  onPostForCharge?: (chargeId: string, payerId: number | null) => void
}

const formatDate = (date: string | Date | null | undefined) => formatEasternDate(date, '--')

const formatMoney = (value: number | undefined | null) => {
  return `$${Number(value || 0).toFixed(2)}`
}

const SUBMISSION_LABELS: Record<string, string> = {
  PENDING_SUBMISSION: 'Pending Submission',
  SUBMITTED: 'Submitted',
  FAILED_SUBMISSION: 'Failed Submission',
  NOT_SUBMITTABLE: 'Not Submittable',
}

// A charge auto-expands only when there is something in flight to look at:
// posted activity on a balance that is still open. Fully paid charges and
// charges nothing was posted against start collapsed.
const defaultExpanded = (row: ChargeAccountRow) => row.hasPayments && row.balanceStatus === 'ACTIVE'

export const ChargeAccountTable: React.FC<Props> = ({
  rows,
  isLoading,
  patientId,
  chargesById,
  submittingChargeId,
  onSubmitCob,
  onRefresh,
  onPostForCharge,
}) => {
  const [expandedRows, setExpandedRows] = React.useState<Record<string, boolean>>({})
  const [notesTarget, setNotesTarget] = React.useState<NotesTarget | null>(null)
  const [attachmentsChargeId, setAttachmentsChargeId] = React.useState<string | null>(null)
  // Returning credit works on the whole application, so a line row and the
  // band both address it the same way — by id and full amount.
  const [unapplyTarget, setUnapplyTarget] = React.useState<{ id: string; amount: number } | null>(null)
  const [receiptLoading, setReceiptLoading] = React.useState<Record<string, boolean>>({})
  const canSubmitCharge = useLabPermission(UserPermission.SUBMIT_BILLING_CHARGES)
  const canPostPatientPayments = useLabPermission(UserPermission.POST_PATIENT_PAYMENTS)
  const [unapplyApplication] = useUnapplyBillingPaymentApplicationMutation()
  const [generateReceipt] = useGeneratePaymentReceiptMutation()
  const [getReceiptDownloadUrl] = useLazyGetPaymentReceiptDownloadQuery()
  const { data: procedureDictionary } = useGetAllBillingProceduresListQuery({})

  // Generates a receipt — scoped to a charge (every payment and patient
  // credit posted against it), or the simple receipt for one patient
  // payment — and opens the rendered PDF in a new tab.
  const handleGenerateReceipt = async (scope: { chargeId: string } | { prepaymentId: string }) => {
    const key = 'chargeId' in scope ? scope.chargeId : scope.prepaymentId

    setReceiptLoading((current) => ({ ...current, [key]: true }))

    try {
      const receipt = await generateReceipt({ patientId, ...scope }).unwrap()
      const link = await getReceiptDownloadUrl({ patientId, receiptId: receipt.id }).unwrap()

      if (link?.url) {
        window.open(link.url, '_blank', 'noopener,noreferrer')?.focus()
      }

      if (!receipt.reused) {
        Toast({ type: 'success', label: 'Payment receipt generated' })
      }
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to generate the receipt' })
    } finally {
      setReceiptLoading((current) => ({ ...current, [key]: false }))
    }
  }

  const cptDescriptions = React.useMemo(() => {
    const map = new Map<string, string>()

    for (const procedure of procedureDictionary || []) {
      if (procedure.cptCode) {
        map.set(procedure.cptCode, procedure.description || procedure.name || '')
      }
    }

    return map
  }, [procedureDictionary])

  // User toggles win over the defaults; a reload only fills in charges the
  // user hasn't touched.
  React.useEffect(() => {
    setExpandedRows((current) => {
      const next: Record<string, boolean> = {}

      for (const row of rows) {
        next[row.chargeId] = current[row.chargeId] ?? defaultExpanded(row)
      }

      return next
    })
  }, [rows])

  const notesForTarget = React.useMemo((): BillingNoteDto[] => {
    if (!notesTarget) {
      return []
    }

    if (notesTarget.type === NoteEntity.CHARGE) {
      return rows.find((row) => row.chargeId === notesTarget.entityId)?.notes ?? []
    }

    if (notesTarget.type === NoteEntity.PRE_PAYMENT) {
      for (const row of rows) {
        const application = row.applications.find((candidate) => candidate.id === notesTarget.entityId)

        if (application) {
          return application.notes ?? []
        }

        // Credit that reached a line lives on the line, not in the band.
        for (const serviceLine of row.serviceLines) {
          const lineApplication = (serviceLine.patientApplications || [])
            .find((candidate) => candidate.applicationId === notesTarget.entityId)

          if (lineApplication) {
            return lineApplication.notes ?? []
          }
        }
      }

      return []
    }

    for (const row of rows) {
      for (const line of row.serviceLines) {
        for (const transaction of line.transactions) {
          if (transaction.id === notesTarget.entityId) {
            return transaction.notes ?? []
          }
        }
      }
    }

    return []
  }, [rows, notesTarget])

  const unapplyTargetAmount = unapplyTarget ? Number(Number(unapplyTarget.amount || 0).toFixed(2)) : 0

  const handleUnapplyApplication = async (note?: string) => {
    if (!unapplyTarget) {
      return
    }

    try {
      await unapplyApplication({ patientId, applicationId: unapplyTarget.id, note }).unwrap()
      Toast({ type: 'success', label: 'Payment returned to unapplied' })
      setUnapplyTarget(null)
      onRefresh?.()
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to return the payment to unapplied' })
    }
  }

  if (isLoading) {
    return <div css={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>Loading...</div>
  }

  if (rows.length === 0) {
    return (
      <div css={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>No charges found</div>
    )
  }

  return (
    <>
    <BillingNotesModal
      target={notesTarget}
      notes={notesForTarget}
      onClose={() => setNotesTarget(null)}
      onChange={() => onRefresh?.()}
    />
    <ChargeAttachmentsModal
      chargeId={attachmentsChargeId}
      onClose={() => setAttachmentsChargeId(null)}
      onChange={() => onRefresh?.()}
    />
    <RefundConfirmModal
      show={Boolean(unapplyTarget)}
      title="Return Payment to Unapplied"
      description={
        `This will take ${formatMoney(unapplyTargetAmount)} back off this charge and return it to the `
        + "patient's unapplied balance, where it can be applied to another charge or refunded. "
        + 'Money from a statement payment becomes a credit of its own there. '
        + 'This charge will stop taking prepaid credit automatically, so settle its balance another '
        + 'way — a write-off, another payer, or by applying credit to it by hand.'
      }
      onClose={() => setUnapplyTarget(null)}
      onConfirm={handleUnapplyApplication}
    />
    <Table>
      <TableHeader>
        <tr>
          <TableHeaderCell>Date</TableHeaderCell>
          <TableHeaderCell>Charge</TableHeaderCell>
          <TableHeaderCell>Status</TableHeaderCell>
          <TableHeaderCell>Provider / Billing Entity</TableHeaderCell>
          <TableHeaderCell>Responsible Plan</TableHeaderCell>
          <TableHeaderCell>Charged</TableHeaderCell>
          <TableHeaderCell>Plan Balance</TableHeaderCell>
          <TableHeaderCell>Patient Balance</TableHeaderCell>
          <TableHeaderCell>Plan Paid</TableHeaderCell>
          <TableHeaderCell>Patient Paid</TableHeaderCell>
          <TableHeaderCell>Notes / Files</TableHeaderCell>
          <TableHeaderCell css={{ textAlign: 'right' }}>Actions</TableHeaderCell>
        </tr>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const isExpanded = Boolean(expandedRows[row.chargeId])
          const charge = chargesById[row.chargeId]
          const isPaid = row.balanceStatus === 'PAID'

          return (
            <React.Fragment key={row.chargeId}>
              <TableRow
                css={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f8f9fa' } }}
                onClick={() => {
                  setExpandedRows((current) => ({ ...current, [row.chargeId]: !current[row.chargeId] }))
                }}>
                <TableCell>
                  <div css={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div css={{ width: 16, color: '#495057' }}>
                      <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                    </div>
                    {formatCalendarDate(row.chargeDate, '--')}
                  </div>
                </TableCell>
                <TableCell>
                  <div
                    onClick={(event) => {
                      event.stopPropagation()
                    }}>
                    <Link
                      to={`${routes.index}${routes.patient.root}/${patientId}${routes.billing.root}${routes.billing.charge}${routes.billing.edit}${row.chargeId}`}
                      style={{ textDecoration: 'none' }}>
                      <span style={chargeBadgeStyle}>{getShortChargeIdValue(row.chargeId)}</span>
                    </Link>
                  </div>
                </TableCell>
                <TableCell>
                  <div>{SUBMISSION_LABELS[row.submissionStatus] || row.submissionStatus || '--'}</div>
                  <span
                    css={{
                      backgroundColor: isPaid ? '#28a745' : '#fd7e14',
                      color: 'white',
                      padding: '0.1rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}>
                    {isPaid ? 'PAID' : 'ACTIVE'}
                  </span>
                  {/* Without this there is no way to tell why prepaid credit
                      stops landing on a charge a biller un-applied by hand. */}
                  {row.autoApplyCreditBlocked && (
                    <span
                      title={
                        'A payment was returned to unapplied from this charge, so it no longer takes '
                        + 'prepaid credit automatically. Credit can still be applied to it by hand.'
                      }
                      css={{
                        marginLeft: '0.35rem',
                        border: '1px solid #9370FF',
                        color: '#9370FF',
                        padding: '0.1rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      NO AUTO-CREDIT
                    </span>
                  )}
                  {/* An open denial posts nothing, so nothing else on this tab
                      shows it; it is worked on the charge page. */}
                  {row.openDenialsCount > 0 && (
                    <span
                      title={row.openDenialsCount > 1
                        ? `${row.openDenialsCount} open denials: open the charge to work them`
                        : 'Open denial: open the charge to work it'}
                      onClick={(event) => event.stopPropagation()}
                      css={{ marginLeft: '0.35rem' }}>
                      <Link
                        to={`${routes.index}${routes.patient.root}/${patientId}${routes.billing.root}${routes.billing.charge}${routes.billing.edit}${row.chargeId}`}
                        style={{ textDecoration: 'none' }}>
                        <span
                          css={{
                            border: '1px solid #dc3545',
                            color: '#dc3545',
                            padding: '0.1rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}>
                          DENIAL
                        </span>
                      </Link>
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div>{row.providerName ? nc(row.providerName) : '--'}</div>
                  <div css={{ color: '#6c757d', fontSize: 12 }}>{getRenderingAndBilling(row.billingEntity)}</div>
                </TableCell>
                <TableCell>
                  <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.15rem' }}>
                    <span>{row.type === 'PATIENT' ? 'Self-pay' : row.responsiblePlanName || 'Patient'}</span>
                    {row.responsibleState && row.responsibleState !== 'PLAN_PENDING' && (
                      <StatePill
                        color={responsibilityColor(row.responsibleState)}
                        label={RESPONSIBILITY_LABELS[row.responsibleState] || row.responsibleState}
                        tooltip={row.responsibleState === 'CROSSED_OVER'
                          ? `Forwarded to ${row.responsiblePlanName || 'the next payer'}`
                          : undefined}
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell>{formatMoney(row.totalCharged)}</TableCell>
                <TableCell>{formatMoney(row.planBalance)}</TableCell>
                <TableCell>{formatMoney(row.patientBalance)}</TableCell>
                <TableCell>{formatMoney(row.appliedPlanAmount)}</TableCell>
                <TableCell>{formatMoney(row.appliedPatientAmount)}</TableCell>
                <TableCell>
                  <div css={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                    <NotesIndicator
                      count={(row.notes ?? []).length}
                      isOpen={notesTarget?.entityId === row.chargeId}
                      onToggle={() => setNotesTarget({
                        type: NoteEntity.CHARGE,
                        entityId: row.chargeId,
                        title: `Notes — charge ${getShortChargeIdValue(row.chargeId)}`,
                      })}
                    />
                    <AttachmentsIndicator
                      count={row.attachmentsCount ?? 0}
                      isOpen={attachmentsChargeId === row.chargeId}
                      onToggle={() => setAttachmentsChargeId(row.chargeId)}
                    />
                  </div>
                </TableCell>
                <TableCell css={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    className="btn btn-outline-secondary btn-sm mr-2"
                    disabled={Boolean(receiptLoading[row.chargeId])}
                    title="Print a payment receipt for this charge"
                    onClick={(event) => {
                      event.stopPropagation()
                      handleGenerateReceipt({ chargeId: row.chargeId })
                    }}>
                    {receiptLoading[row.chargeId] ? 'Preparing…' : 'Receipt'}
                  </button>
                  {/* cobRank is the hop the next claim goes to, cobStatus
                      whether that hop still needs one. Both come from the
                      server. */}
                  {charge?.cobStatus === 'SUBMITTED' ? (
                    <>
                      <span css={{ color: '#28a745', fontWeight: 600, fontSize: 12 }}>
                        {charge?.cobRank === 'tertiary' ? 'Tertiary COB claim submitted' : 'COB claim submitted'}
                      </span>
                      {/* Accepted by Optum, so it can be sent again — the payer
                          rejected it, or it has to be corrected. Not while it
                          is still on its way, and not a paper claim. */}
                      {canSubmitCharge
                        && charge.claimSubmissionMethod !== 'PAPER'
                        && (charge.cobRank === 'tertiary'
                          ? charge.tertiarySubmissionStatus
                          : charge.secondarySubmissionStatus) === 'SUBMITTED' && (
                        <button
                          className="btn btn-sm btn-link ml-2"
                          css={{ fontSize: 12, padding: 0 }}
                          disabled={submittingChargeId === row.chargeId}
                          onClick={(event) => {
                            event.stopPropagation()
                            onSubmitCob(charge, { resubmit: true })
                          }}>
                          {submittingChargeId === row.chargeId ? 'Submitting…' : 'Re-submit'}
                        </button>
                      )}
                    </>
                  ) : charge?.cobStatus === 'CROSSED_OVER' && canSubmitCharge ? (
                    <button
                      className="btn btn-sm btn-link"
                      css={{ fontSize: 12, padding: 0 }}
                      disabled={submittingChargeId === row.chargeId}
                      title={charge.cobBlockReason || ''}
                      onClick={(event) => {
                        event.stopPropagation()
                        onSubmitCob(charge)
                      }}>
                      {submittingChargeId === row.chargeId
                        ? 'Submitting…'
                        : getCobActionLabel(charge, { anyway: true })}
                    </button>
                  ) : charge && canSubmitCharge ? (
                    <button
                      className={`btn btn-sm ${charge.cobEligible ? 'btn-primary' : 'btn-outline-secondary'}`}
                      disabled={!charge.cobEligible || submittingChargeId === row.chargeId}
                      title={charge.cobEligible
                        ? getCobActionTitle(charge)
                        : (charge.cobBlockReason || '')}
                      onClick={(event) => {
                        event.stopPropagation()
                        onSubmitCob(charge)
                      }}>
                      {submittingChargeId === row.chargeId
                        ? 'Submitting…'
                        : getCobActionLabel(charge)}
                    </button>
                  ) : null}
                </TableCell>
              </TableRow>
              {isExpanded && (
                <TableRow>
                  <ExpandedCell colSpan={12}>
                    <div>
                      <div
                        css={{
                          ...serviceLineGrid,
                          padding: '0.5rem 0',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#6c757d',
                          borderBottom: '1px solid #dee2e6',
                        }}>
                        <div />
                        <div>CPT</div>
                        <div>St. DOS</div>
                        <div>Charged</div>
                        <div>Balance</div>
                        <div>Exp. Allowed</div>
                        <div>Allowed</div>
                        <div>Paid/Refund</div>
                        <div>Transfer</div>
                        <div>Patient Resp.</div>
                        {/* Payer-side only: it settles the payer's own gap.
                            Forgiving the PATIENT's share is the separate
                            Write Off action on the line. */}
                        <div
                          title="Payer-side write-off — reduces the plan balance, never the patient's. To forgive a patient balance, use the line's Write Off action."
                          css={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
                          Write-off
                        </div>
                        <div>Plan Bal.</div>
                        <div>Patient Bal.</div>
                        <div />
                      </div>
                      {row.serviceLines.length === 0 && (
                        <div css={{ padding: '1rem', textAlign: 'center', color: '#6c757d', fontSize: '0.875rem' }}>
                          No procedures on this charge
                        </div>
                      )}
                      {/* Sorted for display only: a new payment's lines are built from
                          row.serviceLines and must stay in the charge's own order. */}
                      {sortByDateOfService(row.serviceLines, (line) => line.startDos).map((serviceLine, slIndex) => (
                        <ServiceLineRow
                          key={`${row.chargeId}-${serviceLine.cptCode}-${slIndex}`}
                          serviceLine={serviceLine}
                          patientId={patientId}
                          chargeId={row.chargeId}
                          payerChain={charge ? {
                            primaryPayerId: charge.primaryPayerId,
                            primaryPayerName: charge.primaryPayerName,
                            secondaryPayerId: charge.secondaryPayerId,
                            secondaryPayerName: charge.secondaryPayerName,
                          } : null}
                          cptDescription={cptDescriptions.get(serviceLine.cptCode)}
                          refetchPayments={() => {
                            onRefresh?.()
                          }}
                          onPostForCharge={onPostForCharge && row.serviceLines.length > 1
                            ? (payerId) => onPostForCharge(row.chargeId, payerId)
                            : undefined}
                          onPrepaymentReceipt={(prepaymentId) => handleGenerateReceipt({ prepaymentId })}
                          onOpenTransactionNotes={(transaction) => setNotesTarget({
                            type: NoteEntity.PAYMENT_TRANSACTION,
                            entityId: transaction.id!,
                            title: `Notes — ${serviceLine.cptCode || 'service line'} transaction`,
                          })}
                          onOpenApplicationNotes={(application) => setNotesTarget({
                            type: NoteEntity.PRE_PAYMENT,
                            entityId: application.applicationId,
                            title: `Notes — ${application.sourceLabel}`,
                          })}
                          onReturnToUnapplied={(application) => setUnapplyTarget({
                            id: application.applicationId,
                            amount: Number(application.applicationAmount || 0),
                          })}
                        />
                      ))}
                      {/* What reached no line: credit returned to unapplied,
                          credit beyond what the patient owes, and credit on a
                          charge carrying no procedures. */}
                      {row.applications.length > 0 && (
                        <div css={{ marginTop: '0.75rem' }}>
                          <div
                            css={{
                              padding: '0.35rem 0',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                              color: '#6c757d',
                              borderBottom: '1px solid #dee2e6',
                            }}>
                            Pre-payments
                          </div>
                          {row.applications.map((application) => (
                            <div
                              key={application.id}
                              css={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                padding: '0.6rem 0.25rem',
                                borderBottom: '1px solid #dee2e6',
                                fontSize: '0.875rem',
                              }}>
                              <div css={{ minWidth: 90 }}>{formatDate(application.effectiveDate)}</div>
                              <div css={{ flex: 1 }}>
                                <div css={{ fontWeight: 500 }}>{application.sourceLabel}</div>
                                {application.note && (
                                  <div css={{ color: '#6c757d', fontSize: 12 }}>{application.note}</div>
                                )}
                                {application.refundedAt && (
                                  <div css={{ color: '#9370FF', fontSize: 12 }}>
                                    Unapplied {formatDate(application.refundedAt)}
                                    {application.refundNote ? ` — ${application.refundNote}` : ''}
                                  </div>
                                )}
                              </div>
                              {/* Pre-payments are patient-side only — a plan
                                  split was a legacy ability. */}
                              <div>{formatMoney(application.appliedPatientAmount)}</div>
                              <NotesIndicator
                                count={(application.notes ?? []).length}
                                isOpen={notesTarget?.entityId === application.id}
                                onToggle={() => setNotesTarget({
                                  type: NoteEntity.PRE_PAYMENT,
                                  entityId: application.id,
                                  title: `Notes — ${application.sourceLabel}`,
                                })}
                              />
                              {canPostPatientPayments
                                && (application.sourceType === 'PREPAYMENT' || application.sourceType === 'STATEMENT')
                                && !application.refundedAt
                                && Number(application.appliedPatientAmount || 0) + Number(application.appliedPlanAmount || 0) > 0 && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    setUnapplyTarget({
                                      id: application.id,
                                      amount: Number(application.appliedPatientAmount || 0)
                                        + Number(application.appliedPlanAmount || 0),
                                    })
                                  }}>
                                  Return to Unapplied
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </ExpandedCell>
                </TableRow>
              )}
            </React.Fragment>
          )
        })}
      </TableBody>
    </Table>
    </>
  )
}
